/**
 * Part 26, Task 94 — die Übersetzung zwischen Formular und Regel-Rumpf (spec §1.4).
 *
 * Reine Funktionen: Eingaben lesen, Rumpf bauen, Fingerabdruck bilden. Was eine Regel *bewirkt*,
 * rechnet ausschließlich der Server (`POST /regeln/dry-run`); hier wird nichts über Provisionen
 * entschieden. `abdruck()` ist die Sperre vor dem Speichern: nur wenn der Fingerabdruck des geprüften
 * Rumpfs gleich dem des aktuellen Formulars ist, darf gespeichert werden.
 */
import type { RegelZeile as ServerRegelZeile } from './api'
import {
  bpAusProzent as bpAusProzentZahl, centsAusEingabe as centsStreng,
  eingabeAusCents as eingabeAusCentsStreng, prozentAusBp,
} from './geld'

export type EmpfaengerModus = 'deal_role' | 'fixed'
export type DealRolle = 'closer' | 'setter' | 'host' | 'sonstige'
export type BetragsModus = 'fixed' | 'pct_of_net' | 'deal_host_rate' | 'deal_overhead_individual'
export type Anwendung = 'first_rate' | 'every_rate'
export type BedingungsOperator = 'eq' | 'ne' | 'gt' | 'in'
/** egal | extern (selbst geholt) | intern (von der Firma gestellt) — wird zu genau einer Bedingung auf `deal.lead_source`. */
export type LeadQuelle = 'egal' | 'extern' | 'intern'
/** Die eine Frage, die das Formular zuerst stellt: wer bekommt? Drei Rollen des Abschlusses oder eine feste Person. */
export type Rollenwahl = DealRolle | 'fixed'

/**
 * Die Felder einer Regelzeile, die das Formular liest — die Serverzeile selbst (`RegelZeile` in
 * api.ts, engine/types.ts `RuleRow`) ohne die beiden Spalten, die nur die Herkunft festhalten.
 * Kein Nachbau: eine Umbenennung am Server bricht hier, statt still zu driften.
 */
export type RegelZeile = Omit<ServerRegelZeile, 'created_by' | 'created_at'>

export interface BedingungFormular {
  field: string
  op: BedingungsOperator
  value: string
}

export interface RegelFormular {
  id: string | null
  key: string
  name: string
  role_code: string
  role_label: string
  leistung_text: string
  alle_produkte: boolean
  product_codes: string[]
  recipient_mode: EmpfaengerModus
  recipient_deal_role: DealRolle | null
  recipient_tc_id: string | null
  amount_mode: BetragsModus
  betrag_eingabe: string
  prozent_eingabe: string
  applies_to: Anwendung
  lead_quelle: LeadQuelle
  /** Die übrigen Bedingungen — die Lead-Quelle steht in `lead_quelle`, nie hier. */
  conditions: BedingungFormular[]
  unassigned_policy: 'emit' | 'skip'
  valid_from: string
  valid_to: string
  sort_order: number
}

/** Die Felder, auf die eine Bedingung zeigen darf (schemas/catalogue.ts, conditionSchema). */
export const BEDINGUNGS_FELDER: ReadonlyArray<{ field: string; label: string; art: 'person' | 'zahl' | 'text' }> = [
  { field: 'deal.closer_tc_id', label: 'Closer des Abschlusses', art: 'person' },
  { field: 'deal.setter_tc_id', label: 'Setter des Abschlusses', art: 'person' },
  { field: 'deal.host_tc_id', label: 'Empfehlungsgeber des Abschlusses', art: 'person' },
  { field: 'deal.host_rate_bp', label: 'Empfehlungsgeber-Prozent des Abschlusses (Basispunkte)', art: 'zahl' },
  { field: 'deal.overhead_individual_cents', label: 'Overhead individuell (Cent)', art: 'zahl' },
  { field: 'deal.lead_source', label: 'Lead-Quelle (intern/extern)', art: 'text' },
  { field: 'deal.sonstige_tc_id', label: 'Sonstige Rolle des Abschlusses', art: 'person' },
]

export const EMPFAENGER_ROLLE_TEXT: Readonly<Record<DealRolle, string>> = {
  closer: 'Closer des Abschlusses',
  setter: 'Setter des Abschlusses',
  host: 'Empfehlungsgeber des Abschlusses',
  sonstige: 'Sonstige Rolle des Abschlusses',
}

export const BETRAGS_MODUS_TEXT: Readonly<Record<BetragsModus, string>> = {
  fixed: 'Fester Betrag',
  pct_of_net: 'Prozent vom Netto',
  deal_host_rate: 'Prozentsatz laut Abschluss (Empfehlungsgeber-Prozent)',
  deal_overhead_individual: 'Overhead individuell laut Abschluss',
}

export const LEAD_QUELLE_TEXT: Readonly<Record<LeadQuelle, string>> = {
  egal: 'egal — immer',
  extern: 'nur bei selbst geholtem Lead (extern, per FinSuites-Tag)',
  intern: 'nur bei Lead von der Firma (intern — der Standard)',
}

export const ROLLENWAHL_TEXT: Readonly<Record<Rollenwahl, string>> = {
  closer: 'Closer',
  setter: 'Setter',
  host: 'Empfehlungsgeber',
  sonstige: 'Sonstige Rolle (z. B. Host)',
  fixed: 'Feste Person oder Firma',
}

/**
 * Was eine Rolle für die Maschine bedeutet. Der Benutzer wählt „Closer"; Rollen-Code,
 * Rollenbezeichnung, Leistungstext und das Verhalten ohne Empfänger folgen daraus — und bleiben unter
 * „Erweitert" änderbar. Closer: `emit`, weil ein Abschluss ohne Closer nie Absicht ist und als „Rolle
 * fehlt" in die Prüfliste gehört. Setter und Empfehlungsgeber: `skip`, weil ein Abschluss ohne sie
 * schlicht keinen hat.
 */
export const ROLLEN_VORGABE: Readonly<Record<Rollenwahl, {
  recipient_mode: EmpfaengerModus
  recipient_deal_role: DealRolle | null
  role_code: string
  role_label: string
  leistung_text: string
  unassigned_policy: 'emit' | 'skip'
}>> = {
  closer: { recipient_mode: 'deal_role', recipient_deal_role: 'closer', role_code: 'CLOSER', role_label: 'Closer', leistung_text: 'Vermittlungsprovision', unassigned_policy: 'emit' },
  setter: { recipient_mode: 'deal_role', recipient_deal_role: 'setter', role_code: 'SETTER', role_label: 'Setter', leistung_text: 'Vermittlungsprovision', unassigned_policy: 'skip' },
  host: { recipient_mode: 'deal_role', recipient_deal_role: 'host', role_code: 'HOST', role_label: 'Empfehlungsgeber', leistung_text: 'Vermittlungsprovision', unassigned_policy: 'skip' },
  // Die vierte Rolle heißt, wie der Auftraggeber sie nennt — „Host“ bei BestLife. Bezeichnung und Code
  // folgen seiner Eingabe; die Vorgabe ist nur ein Platzhalter, damit das Formular nie leer speichert.
  sonstige: { recipient_mode: 'deal_role', recipient_deal_role: 'sonstige', role_code: 'SONSTIGE', role_label: 'Sonstige', leistung_text: 'Vermittlungsprovision', unassigned_policy: 'skip' },
  fixed: { recipient_mode: 'fixed', recipient_deal_role: null, role_code: 'BETEILIGUNG', role_label: 'Beteiligung', leistung_text: 'Beteiligung (Umsatzbeteiligung)', unassigned_policy: 'emit' },
}

export function rollenwahl(formular: RegelFormular): Rollenwahl {
  return formular.recipient_mode === 'fixed' ? 'fixed' : (formular.recipient_deal_role ?? 'closer')
}

/** Die Felder, die ein Rollenwechsel setzt. Empfänger-Person bleibt, wo sie war. */
export function waehleRolle(wahl: Rollenwahl): Partial<RegelFormular> {
  return { ...ROLLEN_VORGABE[wahl] }
}

/**
 * Der Schlüssel aus dem Namen: Großbuchstaben, Ziffern und Unterstrich, Umlaute übersetzt, beginnt mit
 * einem Buchstaben — genau die Grammatik von `ruleFields.key` (schemas/catalogue.ts). Der Benutzer sieht
 * ihn unter „Erweitert" und kann ihn ändern; von selbst tippt ihn niemand mehr.
 */
export function schluesselAus(name: string): string {
  const roh = name
    .toUpperCase()
    .replace(/Ä/g, 'AE').replace(/Ö/g, 'OE').replace(/Ü/g, 'UE').replace(/ß/g, 'SS')
    .replace(/%/g, ' PROZENT ')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64)
  if (roh === '') return ''
  return /^[A-Z]/.test(roh) ? roh : `R_${roh}`.slice(0, 64)
}

function betragText(formular: RegelFormular): string {
  switch (formular.amount_mode) {
    case 'pct_of_net': return formular.prozent_eingabe.trim() === '' ? '' : `${formular.prozent_eingabe.trim()} %`
    case 'fixed': return formular.betrag_eingabe.trim() === '' ? '' : `${formular.betrag_eingabe.trim()} €`
    case 'deal_host_rate': return 'Prozent laut Abschluss'
    case 'deal_overhead_individual': return 'Overhead individuell'
  }
}

/**
 * Name und Schlüssel, wie das Formular sie vorschlägt: „Closer 10 % — jede Rate — Lead gestellt".
 * Nur ein Vorschlag — wer den Namen ändert, behält seinen (regeln.tsx merkt sich, was vorgeschlagen war).
 */
export function ableiten(formular: RegelFormular): { name: string; key: string } {
  const rolle = formular.recipient_mode === 'fixed'
    ? (formular.role_label.trim() === '' ? ROLLENWAHL_TEXT.fixed : formular.role_label.trim())
    : ROLLENWAHL_TEXT[rollenwahl(formular)]
  const teile = [
    [rolle, betragText(formular)].filter((t) => t !== '').join(' '),
    ANWENDUNG_TEXT[formular.applies_to],
    formular.lead_quelle === 'intern' ? 'Lead gestellt' : formular.lead_quelle === 'extern' ? 'Lead selbst geholt' : '',
  ].filter((t) => t !== '')
  const name = teile.join(' — ')
  return { name, key: schluesselAus(name) }
}

/**
 * Die Vorlagen unter „Neue Regel": was fast jeder braucht, in einem Klick. Sie füllen das Formular
 * vor und sonst nichts; gespeichert wird erst nach dem Trockenlauf, wie bei jeder Regel.
 */
export interface Vorlage {
  id: string
  text: string
  werte: Partial<RegelFormular>
}

const JEDE = { applies_to: 'every_rate' as const, amount_mode: 'pct_of_net' as const, alle_produkte: true, lead_quelle: 'egal' as const }
const ERSTE = { ...JEDE, applies_to: 'first_rate' as const }

export const VORLAGEN: readonly Vorlage[] = [
  { id: 'closer_jede', text: 'Closer 10 % — jede Rate', werte: { ...waehleRolle('closer'), ...JEDE, prozent_eingabe: '10' } },
  { id: 'closer_erste', text: 'Closer 10 % — einmalig, nur 1. Rate', werte: { ...waehleRolle('closer'), ...ERSTE, prozent_eingabe: '10' } },
  { id: 'setter_jede', text: 'Setter 5 % — jede Rate', werte: { ...waehleRolle('setter'), ...JEDE, prozent_eingabe: '5' } },
  { id: 'setter_erste', text: 'Setter 5 % — einmalig, nur 1. Rate', werte: { ...waehleRolle('setter'), ...ERSTE, prozent_eingabe: '5' } },
  { id: 'empfehlung_jede', text: 'Empfehlungsgeber 2,5 % — jede Rate', werte: { ...waehleRolle('host'), ...JEDE, prozent_eingabe: '2,5' } },
  { id: 'empfehlung_erste', text: 'Empfehlungsgeber 2,5 % — einmalig, nur 1. Rate', werte: { ...waehleRolle('host'), ...ERSTE, prozent_eingabe: '2,5' } },
  {
    id: 'overhead_individuell', text: 'Overhead individuell laut Abschluss — feste Person, jede Rate',
    werte: {
      ...waehleRolle('fixed'), role_code: 'OVERHEAD', role_label: 'Overhead', leistung_text: 'Overhead-Provision (Teamleitung)',
      amount_mode: 'deal_overhead_individual', applies_to: 'every_rate', alle_produkte: true, lead_quelle: 'egal',
    },
  },
  { id: 'fest_prozent', text: 'Feste Person oder Firma — Prozent vom Netto, jede Rate', werte: { ...waehleRolle('fixed'), ...JEDE } },
]

export const ANWENDUNG_TEXT: Readonly<Record<Anwendung, string>> = {
  first_rate: 'nur 1. Rate',
  every_rate: 'jede Rate',
}

/**
 * Ein Vorzeichen davor, der Rest durch die strenge Grammatik aus src/client/geld.ts (und damit aus
 * src/shared/money.ts — dieselbe, mit der der Worker FinSuites-Beträge liest). Sie kennt kein Minus
 * (dort dokumentiert), eine Provisionsregel darf aber abziehen; also wird das Minus hier abgetrennt
 * und nach dem Lesen wieder angesetzt. Eine zweite Zahlengrammatik im Client gibt es nicht: eine
 * eigene wäre bei „1.234“ um den Faktor 1000 daneben, ohne dass der Server das noch merken könnte.
 */
function hundertstelMitVorzeichen(text: string): number | null {
  const roh = text.trim()
  const negativ = roh.startsWith('-')
  const betrag = centsStreng(negativ ? roh.slice(1).trim() : roh)
  if (betrag === null) return null
  return negativ ? -betrag : betrag
}

/** '50,00' → 5000, '1.234' → 123400, '-12,5' → -1250. Alles Unlesbare ist null; zod meldet es. */
export function centsAusEingabe(text: string): number | null {
  return hundertstelMitVorzeichen(text)
}

/** '2,5' → 250 Basispunkte — dieselbe Grammatik, dieselbe Umrechnung in Hundertstel. */
export function bpAusProzent(text: string): number | null {
  const hundertstel = hundertstelMitVorzeichen(text)
  return hundertstel === null ? null : bpAusProzentZahl(hundertstel / 100)
}

export function eingabeAusCents(cents: number | null): string {
  return cents === null ? '' : eingabeAusCentsStreng(cents)
}

/** 250 → '2,5'. Ohne Tausenderpunkt, damit `bpAusProzent` das Feld wieder lesen kann. */
export function eingabeAusBp(bp: number | null): string {
  return bp === null ? '' : String(prozentAusBp(bp)).replace('.', ',')
}

export function leeresFormular(heute: string): RegelFormular {
  return {
    id: null, key: '', name: '', role_code: '', role_label: '', leistung_text: '',
    alle_produkte: false, product_codes: [], recipient_mode: 'deal_role', recipient_deal_role: 'closer',
    recipient_tc_id: null, amount_mode: 'pct_of_net', betrag_eingabe: '', prozent_eingabe: '',
    applies_to: 'every_rate', lead_quelle: 'egal', conditions: [], unassigned_policy: 'emit',
    valid_from: heute, valid_to: '', sort_order: 100,
  }
}

// Die vier Spalten kommen als `string` aus api.ts, weil dort die Zeile der Datenbank steht. Die
// CHECK-Bedingungen von `commission_rules` lassen genau diese Werte zu; die Rückfallwerte hier sind
// deshalb kein Ersatz für eine Prüfung, sondern nur die Brücke von der Spalte zur Auswahlliste.
function alsEmpfaengerModus(wert: string): EmpfaengerModus {
  return wert === 'fixed' ? 'fixed' : 'deal_role'
}

function alsDealRolle(wert: string | null): DealRolle | null {
  return wert === 'closer' || wert === 'setter' || wert === 'host' || wert === 'sonstige' ? wert : null
}

function alsBetragsModus(wert: string): BetragsModus {
  return wert === 'fixed' || wert === 'deal_host_rate' || wert === 'deal_overhead_individual'
    ? wert
    : 'pct_of_net'
}

function alsAnwendung(wert: string): Anwendung {
  return wert === 'first_rate' ? 'first_rate' : 'every_rate'
}

function bedingungenAusJson(json: string): BedingungFormular[] {
  let roh: unknown
  try {
    roh = JSON.parse(json)
  } catch {
    return []
  }
  if (!Array.isArray(roh)) return []
  return roh.map((eintrag) => {
    const objekt = eintrag as { field?: unknown; op?: unknown; value?: unknown }
    const wert = objekt.value
    return {
      field: typeof objekt.field === 'string' ? objekt.field : '',
      op: objekt.op === 'eq' || objekt.op === 'ne' || objekt.op === 'gt' || objekt.op === 'in' ? objekt.op : 'eq',
      value: Array.isArray(wert) ? wert.map((teil) => String(teil)).join(', ') : String(wert ?? ''),
    }
  })
}

function produktCodesAusJson(json: string | null): string[] {
  if (json === null) return []
  try {
    const roh: unknown = JSON.parse(json)
    return Array.isArray(roh) ? roh.map((code) => String(code)) : []
  } catch {
    return []
  }
}

/**
 * Trennt die eine Bedingung auf `deal.lead_source`, die das Formular als Auswahl zeigt, von den
 * übrigen. `ne intern` und `eq extern` heißen beide „selbst geholt"; alles andere auf dem Feld (etwa
 * ein `in`) bleibt als gewöhnliche Bedingung stehen, statt still zu verschwinden.
 */
export function leadQuelleAus(bedingungen: readonly BedingungFormular[]): { lead_quelle: LeadQuelle; uebrige: BedingungFormular[] } {
  const stelle = bedingungen.findIndex((b) => {
    if (b.field !== 'deal.lead_source') return false
    const wert = b.value.trim().toLowerCase()
    return (b.op === 'eq' || b.op === 'ne') && (wert === 'intern' || wert === 'extern')
  })
  if (stelle === -1) return { lead_quelle: 'egal', uebrige: [...bedingungen] }
  const gefunden = bedingungen[stelle] as BedingungFormular
  const wert = gefunden.value.trim().toLowerCase()
  // `eq intern` und `ne extern` heißen beide intern, `eq extern` und `ne intern` beide extern.
  const lead_quelle: LeadQuelle = (gefunden.op === 'eq') === (wert === 'intern') ? 'intern' : 'extern'
  return { lead_quelle, uebrige: bedingungen.filter((_, i) => i !== stelle) }
}

export function formularAusRegel(regel: RegelZeile): RegelFormular {
  const { lead_quelle, uebrige } = leadQuelleAus(bedingungenAusJson(regel.conditions_json))
  return {
    id: regel.id,
    key: regel.key,
    name: regel.name,
    role_code: regel.role_code,
    role_label: regel.role_label,
    leistung_text: regel.leistung_text,
    alle_produkte: regel.product_codes_json === null,
    product_codes: produktCodesAusJson(regel.product_codes_json),
    recipient_mode: alsEmpfaengerModus(regel.recipient_mode),
    recipient_deal_role: alsDealRolle(regel.recipient_deal_role),
    recipient_tc_id: regel.recipient_tc_id,
    amount_mode: alsBetragsModus(regel.amount_mode),
    betrag_eingabe: eingabeAusCents(regel.amount_cents),
    prozent_eingabe: eingabeAusBp(regel.rate_bp),
    applies_to: alsAnwendung(regel.applies_to),
    lead_quelle,
    conditions: uebrige,
    unassigned_policy: regel.unassigned_policy,
    valid_from: regel.valid_from,
    valid_to: regel.valid_to ?? '',
    sort_order: regel.sort_order,
  }
}

function bedingungsWert(bedingung: BedingungFormular): unknown {
  const feld = BEDINGUNGS_FELDER.find((eintrag) => eintrag.field === bedingung.field)
  const istZahl = feld?.art === 'zahl'
  if (bedingung.op === 'in') {
    const teile = bedingung.value.split(',').map((teil) => teil.trim()).filter((teil) => teil !== '')
    return istZahl ? teile.map((teil) => Number(teil)) : teile
  }
  return istZahl ? Number(bedingung.value.trim()) : bedingung.value.trim()
}

/** Der Rumpf für `upsertRuleSchema` / `dryRunRuleSchema`. Ungültige Zahlen bleiben null — zod meldet sie. */
export function koerperAusFormular(formular: RegelFormular): Record<string, unknown> {
  return {
    id: formular.id,
    key: formular.key,
    name: formular.name,
    role_code: formular.role_code,
    role_label: formular.role_label,
    leistung_text: formular.leistung_text,
    product_codes: formular.alle_produkte ? null : formular.product_codes,
    recipient_mode: formular.recipient_mode,
    recipient_deal_role: formular.recipient_mode === 'deal_role' ? formular.recipient_deal_role : null,
    recipient_tc_id: formular.recipient_mode === 'fixed' ? formular.recipient_tc_id : null,
    amount_mode: formular.amount_mode,
    amount_cents: formular.amount_mode === 'fixed' ? centsAusEingabe(formular.betrag_eingabe) : null,
    rate_bp: formular.amount_mode === 'pct_of_net' ? bpAusProzent(formular.prozent_eingabe) : null,
    applies_to: formular.applies_to,
    conditions: [
      ...formular.conditions.map((bedingung) => ({
        field: bedingung.field, op: bedingung.op, value: bedingungsWert(bedingung),
      })),
      // Intern ist der Standard: `ne extern` trifft auch einen Abschluss ohne Vermerk.
      ...(formular.lead_quelle === 'intern'
        ? [{ field: 'deal.lead_source', op: 'ne', value: 'extern' }]
        : formular.lead_quelle === 'extern'
          ? [{ field: 'deal.lead_source', op: 'eq', value: 'extern' }]
          : []),
    ],
    unassigned_policy: formular.unassigned_policy,
    valid_from: formular.valid_from,
    valid_to: formular.valid_to === '' ? null : formular.valid_to,
    sort_order: formular.sort_order,
  }
}

function stabil(wert: unknown): unknown {
  if (Array.isArray(wert)) return wert.map(stabil)
  if (typeof wert === 'object' && wert !== null) {
    const eintraege = Object.entries(wert as Record<string, unknown>)
      .sort(([links], [rechts]) => links.localeCompare(rechts))
    return Object.fromEntries(eintraege.map(([schluessel, teil]) => [schluessel, stabil(teil)]))
  }
  return wert
}

/** Fingerabdruck eines Rumpfs, unabhängig von der Schlüsselreihenfolge — die Sperre vor dem Speichern. */
export function abdruck(koerper: unknown): string {
  return JSON.stringify(stabil(koerper))
}
